/* Reviewed untested drafts; recipe data is fetched only after an eligible day opens. */
(() => {
  if (window.NanaWorldDrafts) return;
  const catalogs = new WeakMap();
  const requests = new Map();
  const owned = new Map();
  const recordFields = new Set(['day','country','calendarDish','displayTitle','credit','url','yield','ingredients','steps','note','status','untested','conversionPairs']);
  const fail = () => { throw new Error('Cook-along draft data could not be verified.'); };
  const validDay = day => Number.isInteger(day) && day >= 38 && day <= 364;
  const read = root => {
    if (catalogs.has(root)) return catalogs.get(root);
    const node = root.querySelector('[data-nt-world-drafts]');
    if (!node) return null;
    const data = JSON.parse(node.textContent);
    if (data.schemaVersion !== 1 || !data.records || !data.batches || !data.titles) fail();
    const canonical = new Map(JSON.parse(root.querySelector('[data-nt-data]').textContent).map(row => [row.day,row]));
    for (const [key, info] of Object.entries(data.records)) {
      if (!validDay(Number(key)) || String(Number(key)) !== key || !data.batches[info.batchId]) fail();
      if (!['displayTitle','credit','yield','url'].every(x => typeof info[x] === 'string' && info[x].length > 0)) fail();
    }
    const catalog = {data,canonical,pending:new Map()};
    catalogs.set(root,catalog);
    return catalog;
  };
  const info = (root, day) => {
    const catalog = read(root);
    if (!catalog) return null;
    return catalog.data.records[String(day)] || (catalog.data.titles[String(day)] ? {displayTitle:catalog.data.titles[String(day)]} : null);
  };
  const has = (root, day) => Boolean(validDay(Number(day)) && read(root)?.data.records[String(day)]);
  const checkedBatch = (catalog, batchId, data) => {
    const batch = catalog.data.batches[batchId];
    if (!data || data.schemaVersion !== 1 || data.batchId !== batchId || !data.records || Array.isArray(data.records)) fail();
    const expected = batch.days.map(String).sort((a,b)=>Number(a)-Number(b));
    const keys = Object.keys(data.records).sort((a,b)=>Number(a)-Number(b));
    if (JSON.stringify(keys) !== JSON.stringify(expected)) fail();
    const accepted = {};
    for (const key of keys) {
      const recipe = data.records[key], day = Number(key);
      const entry = catalog.data.records[key], row = catalog.canonical.get(day);
      if (!validDay(day) || !entry || entry.batchId !== batchId || !row || !recipe || Array.isArray(recipe)) fail();
      if (Object.keys(recipe).some(x => !recordFields.has(x))) fail();
      if (recipe.day !== day || recipe.country !== row.country || recipe.calendarDish !== row.dish) fail();
      if (recipe.status !== 'draft-untested' || recipe.untested !== true) fail();
      for (const field of ['displayTitle','credit','yield','url']) if (recipe[field] !== entry[field]) fail();
      for (const field of ['displayTitle','credit','url','yield','note']) if (typeof recipe[field] !== 'string' || !recipe[field].trim() || recipe[field].length > 20000) fail();
      const url = new URL(recipe.url);
      if (url.protocol !== 'https:' || url.username || url.password) fail();
      for (const field of ['ingredients','steps']) {
        if (!Array.isArray(recipe[field]) || recipe[field].length < 1 || recipe[field].length > 100 || recipe[field].some(x => typeof x !== 'string' || !x.trim() || x.length > 10000)) fail();
      }
      if (!Array.isArray(recipe.conversionPairs) || recipe.conversionPairs.length < 1 || recipe.conversionPairs.length > 40) fail();
      for (const pair of recipe.conversionPairs) {
        if (!pair || typeof pair !== 'object' || Array.isArray(pair) || Object.keys(pair).sort().join(',') !== 'after,before,function,scope') fail();
        for (const field of ['before','after','function','scope']) if (typeof pair[field] !== 'string' || !pair[field].trim() || pair[field].length > 2000) fail();
      }
      accepted[key] = recipe;
    }
    return accepted;
  };
  const fetchBatch = batch => {
    const url = new URL(batch.url, location.href);
    if (url.protocol !== 'https:' || url.username || url.password || !/^[a-f0-9]{64}$/.test(batch.sha256) || batch.bytes > 180000) fail();
    const key = url.href + ':' + batch.sha256;
    if (requests.has(key)) return requests.get(key);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(),15000);
    const request = (async () => {
      const response = await fetch(url.href,{credentials:'omit',cache:'force-cache',signal:controller.signal});
      if (!response.ok) fail();
      const text = await response.text(), bytes = new TextEncoder().encode(text);
      if (bytes.length !== batch.bytes || bytes.length > 180000 || !crypto.subtle) fail();
      const digest = await crypto.subtle.digest('SHA-256',bytes);
      const hash = [...new Uint8Array(digest)].map(x=>x.toString(16).padStart(2,'0')).join('');
      if (hash !== batch.sha256) fail();
      return JSON.parse(text);
    })().finally(() => clearTimeout(timeout));
    requests.set(key,request);
    request.catch(() => requests.delete(key));
    return request;
  };
  const ensure = (root, day) => {
    const catalog = read(root), entry = catalog?.data.records[String(day)];
    if (!validDay(Number(day)) || !entry) return Promise.resolve(null);
    const target = window.NanaCookAlong;
    if (!target || typeof target !== 'object') return Promise.reject(new Error('Cook-along is not ready.'));
    if (owned.get(Number(day)) === target[String(day)] && target[String(day)]) return Promise.resolve(target[String(day)]);
    const batchId = entry.batchId;
    if (!catalog.pending.has(batchId)) {
      const promise = fetchBatch(catalog.data.batches[batchId]).then(data => {
        const accepted = checkedBatch(catalog,batchId,data);
        // Validate the entire batch before changing the recipe map. Never overwrite existing data.
        for (const [key, recipe] of Object.entries(accepted)) if (target[key] && owned.get(Number(key)) !== target[key]) fail();
        for (const [key, recipe] of Object.entries(accepted)) {target[key]=recipe;owned.set(Number(key),recipe);}
        root.dispatchEvent(new CustomEvent('nana:world-drafts-loaded',{detail:{days:Object.keys(accepted).map(Number)}}));
        return accepted;
      }).finally(() => catalog.pending.delete(batchId));
      catalog.pending.set(batchId,promise);
    }
    return catalog.pending.get(batchId).then(data=>data[String(day)]);
  };
  window.NanaWorldDrafts = Object.freeze({
    has:(root,day)=>{try{return has(root,day);}catch{return false;}},
    info:(root,day)=>{try{return info(root,day);}catch{return null;}},
    ensure:(root,day)=>Promise.resolve().then(()=>ensure(root,day))
  });
})();
